import type { NotificationPriority, NotificationType } from "@/types/api";

export type NotificationTemplate = {
  id: string;
  name: string;
  description?: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  isSystem?: boolean;
};

/**
 * General-purpose templates that are safe across industries (trucking + service/repair).
 * These are meant to be "admin broadcast" style messages.
 */
export const DEFAULT_NOTIFICATION_TEMPLATES: NotificationTemplate[] = [
  {
    id: "system-maintenance",
    name: "Scheduled maintenance",
    description:
      "Let users know about planned downtime or degraded performance.",
    type: "SYSTEM",
    priority: "medium",
    title: "Scheduled maintenance",
    message:
      "Heads up: scheduled maintenance is planned for today. Some features may be temporarily unavailable.",
    metadata: { template: "system-maintenance" },
    isSystem: true,
  },
  {
    id: "policy-update",
    name: "Policy / process update",
    description: "Communicate a general process change or policy reminder.",
    type: "SYSTEM",
    priority: "low",
    title: "Process update",
    message:
      "Reminder: please review the latest process update in Settings → Help/Docs. Thanks!",
    metadata: { template: "policy-update" },
    isSystem: true,
  },
  {
    id: "dispatch-reminder",
    name: "Dispatch reminder",
    description:
      "General dispatch reminder (useful for both trucking dispatch and service scheduling).",
    type: "DISPATCH",
    priority: "high",
    title: "Dispatch reminder",
    message:
      "Please review your assignments and confirm your availability. If you need help, contact dispatch.",
    metadata: { template: "dispatch-reminder" },
    isSystem: true,
  },
  {
    id: "invoice-reminder",
    name: "Billing reminder",
    description: "General invoice/billing reminder.",
    type: "INVOICE",
    priority: "low",
    title: "Billing reminder",
    message:
      "Friendly reminder: please review any outstanding invoices or billing items when you have a moment.",
    metadata: { template: "invoice-reminder" },
    isSystem: true,
  },
  {
    id: "emergency-alert",
    name: "Emergency alert",
    description: "Urgent broadcast for safety or critical incidents.",
    type: "EMERGENCY",
    priority: "urgent",
    title: "Emergency alert",
    message:
      "Emergency notice: please follow your safety procedures and contact your supervisor/dispatch immediately if you need assistance.",
    metadata: { template: "emergency-alert" },
    isSystem: true,
  },
];

